import {
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Select
} from '@chakra-ui/react'
import type { PersonDto } from 'BrokerTsLib/src/dtos'
import { useFormContext } from 'BrokerTsLib/src/rhf'
import { subYears } from 'date-fns'
import type { ChangeEvent } from 'react'

import { InputGroup } from '@/components/InputGroup'
import { ReactHookFormDatepicker as Datepicker } from '@/containers/forms/Datepicker'

import { useCountries } from '../../../hooks'
import { useNameof } from '../../../hooks/useNameof'
import { FormRow } from '../FormRow'

export const PersonID = ({
  personPrefix,
  disabled,
  fullWidth = false
}: {
  personPrefix: string,
  disabled?: boolean,
  fullWidth?: boolean
}) => {

  const pnameof = useNameof<PersonDto>(personPrefix)

  const { register, getValues, setValue, watch, getFieldState } = useFormContext()
  const doesNotHavePIN = watch(pnameof(x => x.isForeigner))

  const handlePINChange = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked

    setValue(pnameof(x => x.isForeigner), checked)
  }

  const { data: countries } = useCountries()
  const idError = getFieldState(doesNotHavePIN ? pnameof(x => x.birthday) : pnameof(x => x.birthNumber))?.error

  const now = new Date()

  return (
    <FormRow itemsCount={fullWidth && !doesNotHavePIN ? 1 : 2}>
      <FormControl
        isDisabled={disabled}
        isInvalid={!!idError}
      >
        <Flex
          sx={{
            w: '100%',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <FormLabel htmlFor={doesNotHavePIN ? 'birthDate' : 'pin'}>
            {doesNotHavePIN ? 'Datum narození' : 'Rodné číslo'}
          </FormLabel>

          {!disabled && <Checkbox isChecked={doesNotHavePIN} onChange={handlePINChange}>
            Nemá RČ
          </Checkbox>}
        </Flex>

        {!doesNotHavePIN && (
          <InputGroup
            id="pin"
            placeholder="##########"
            errors={idError}
            isDisabled={disabled}
            {...register(pnameof(x => x.birthNumber))}
          />
        )}

        {doesNotHavePIN &&
          <Datepicker
            name={pnameof(x => x.birthday)}
            autoComplete="bday"
            placeholder="DD.MM.RRRR"
            isDisabled={disabled}
            options={{
              minDate: subYears(now, 120),
              maxDate: now
            }
            } />
        }

        {!doesNotHavePIN && !disabled && (
          <FormHelperText>
            Rodné číslo může obsahovat pouze číslice bez mezer, lomítek,
            pomlček. U klientů, kteří ho nemají přiděleno (cizí státní
            příslušníci), zaškrtněnte volbu "Nemá RČ" a zadejte datum narození a
            státní občanství.
          </FormHelperText>
        )}
      </FormControl>

      {doesNotHavePIN && (
        <FormControl isInvalid={!!getFieldState(pnameof(x => x.nationalityId))?.error}>
          <FormLabel>Státní občanství</FormLabel>

          {countries?.data && countries?.data.length > 0 &&
            <Select {...register(pnameof(x => x.nationalityId))} defaultValue={getValues(pnameof(x => x.nationalityId)) ?? ''} isDisabled={disabled}>
              <option disabled value=''>
                Vyberte státní občanství
              </option>
              {countries?.data.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          }
        </FormControl>
      )}
    </FormRow>
  )
}
